/**
 * Live Trading V3 — the Attack (W/E) arm state and size.
 *
 * Attack is the deliberately AGGRESSIVE hotkey lane: one bounded marketable
 * limit at the level above the passive reference, allowed to take liquidity.
 * It is armed SEPARATELY from — and only ever in addition to — the ordinary
 * one-click arm:
 *
 *   • Arming is SESSION-ONLY and never persisted, exactly like the one-click
 *     arm. Refresh, access loss, and the toggle disarm it. A board change
 *     that disarms one-click drops it too, and re-arming one-click brings it
 *     back (B6).
 *   • Attack arms only while one-click is armed, and DISARMS ITSELF whenever
 *     one-click disarms: the big red disarm remains the one master kill for
 *     every hot input path, so no keyboard action can survive it.
 *   • The attack size is its own explicit, persisted setting — never the
 *     one-click size. The packet's rule that the effective size is visible at
 *     the interaction surface applies to each lane separately, and a trader
 *     quoting 500 must be able to attack 50 without editing either.
 *   • Depth is FIXED at ATTACK_DEPTH_CONTRACT (1) in this release. The
 *     control renders the value read-only; the relay refuses anything deeper
 *     regardless (ATTACK_DEPTH_UNSUPPORTED), because depth>1 semantics —
 *     one bounded sweep versus a resting ladder — are an unaccepted product
 *     decision, not a knob.
 */

import { create } from 'zustand';
import { useOneClickStore, _registerAttackStateAccessor } from './oneClickStore';

// The numeric policy lives in the pure constants module so price-derivation
// code can import it without dragging this store's subscription graph into
// every resolver's import chain. Re-exported here for the control/store
// consumers that already have the store in scope.
export {
  ATTACK_MAX_CONTRACTS,
  ATTACK_FALLBACK_MAX_CONTRACTS,
  ATTACK_DEFAULT_CONTRACTS,
  ATTACK_DEPTH_CONTRACT,
} from '@/lib/trading/attackPolicyClient';
import {
  ATTACK_MAX_CONTRACTS,
  ATTACK_DEFAULT_CONTRACTS,
} from '@/lib/trading/attackPolicyClient';

const SIZE_STORAGE_KEY = 'galactus:attackSizeContracts';

interface AttackStore {
  attackArmed: boolean;
  attackSizeContracts: number;
  armAttack: () => void;
  disarmAttack: () => void;
  setAttackSizeContracts: (n: number) => void;
}

/** Whole contracts within the hard maximum, or null for a non-finite input:
 *  a refusal, never a default (see oneClickStore's clampSize). */
function clampSize(n: number): number | null {
  if (!Number.isFinite(n)) return null;
  return Math.max(1, Math.min(ATTACK_MAX_CONTRACTS, Math.round(n)));
}

function loadSize(): number {
  try {
    const raw = localStorage.getItem(SIZE_STORAGE_KEY);
    // Loading keeps its default for an absent or unreadable stored size.
    return (raw == null ? null : clampSize(parseInt(raw, 10))) ?? ATTACK_DEFAULT_CONTRACTS;
  } catch {
    return ATTACK_DEFAULT_CONTRACTS;
  }
}

export const useAttackStore = create<AttackStore>((set) => ({
  attackArmed: false,
  attackSizeContracts: loadSize(),

  armAttack: () => {
    // Attack rides ON TOP of the one-click arm; it can never be the only
    // armed state. The control also disables itself, but that is styling,
    // not authority.
    if (!useOneClickStore.getState().armed) return;
    set({ attackArmed: true });
  },
  // B6 — the trader (or access loss) turning Attack off also cancels any
  // pending restore from an earlier board-driven disarm.
  disarmAttack: () => {
    set({ attackArmed: false });
    if (useOneClickStore.getState().attackRestore) {
      useOneClickStore.setState({ attackRestore: false });
    }
  },

  setAttackSizeContracts: (n: number) => {
    const clamped = clampSize(n);
    // Non-finite is refused: the attack size the trader last set stays live.
    if (clamped == null) return;
    set({ attackSizeContracts: clamped });
    try {
      localStorage.setItem(SIZE_STORAGE_KEY, String(clamped));
    } catch {
      /* private mode — session-only size is fine */
    }
  },
}));

// The one-click disarm is the master kill for every hot input path. A
// module-level subscription (not a component effect) so the invariant holds
// on every surface, mounted or not.
//
// B6 — every board-change disarm stays (a mouse one-click fires immediately
// on the new board), but re-arming 1-Click brings back an Attack arm such a
// disarm dropped, once. Only Attack's own state is restored: W/E still check
// the Attack grant on every press, and the relay re-checks it on every order.
useOneClickStore.subscribe((state, prev) => {
  if (prev.armed && !state.armed) {
    useAttackStore.setState({ attackArmed: false });
  } else if (!prev.armed && state.armed && prev.attackRestore) {
    useAttackStore.setState({ attackArmed: true });
  }
});

// Hand the submit lane a lazy view of the attack state without a reverse
// import (see _registerAttackStateAccessor).
_registerAttackStateAccessor(() => {
  const { attackArmed, attackSizeContracts, disarmAttack } = useAttackStore.getState();
  return { attackArmed, attackSizeContracts, disarmAttack };
});
